/**
 * commDragStore.ts
 * Tiny external store that lets the contact Kanban describe the item currently being dragged
 * (person / Vorgang / stack) and the column it hovers. The global DragOverlay in App.tsx reads it
 * to render a morphing preview ("Neuer Vorgang mit …", "3 Vorgänge verschieben", …).
 */
import { useSyncExternalStore } from 'react';

export interface CommDragMeta {
  kind: 'person' | 'vorgang' | 'stack';
  title: string;
  personName?: string;
  organization?: string;
  count?: number;
  sourceColumn: string;
  overColumn?: string | null;
  overZone?: string | null;
}

let current: CommDragMeta | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(l => l());

export const commDragStore = {
  get: () => current,
  set(meta: CommDragMeta | null) {
    current = meta;
    emit();
  },
  patch(update: Partial<CommDragMeta>) {
    if (!current) return;
    const changed = Object.keys(update).some(k => (current as any)[k] !== (update as any)[k]);
    if (!changed) return;
    current = { ...current, ...update };
    emit();
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};

export function useCommDragMeta(): CommDragMeta | null {
  return useSyncExternalStore(commDragStore.subscribe, commDragStore.get, commDragStore.get);
}
